import React, { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Crown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get("next") || "/";

  if (user && user !== false) return <Navigate to={nextPath} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(nextPath);
    else setError(res.error);
  };

  return (
    <div
      className="min-h-screen grid md:grid-cols-2"
      data-testid="login-page"
    >
      {/* Left brand panel */}
      <div
        className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.85)), url('https://images.pexels.com/photos/36217325/pexels-photo-36217325.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 flex items-center justify-center"
            style={{ background: "var(--mj-red)" }}
          >
            <Crown size={24} color="#E5B13A" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-3xl font-bold leading-none">
              Majesty<span style={{ color: "var(--mj-gold)" }}>IO</span>
            </div>
            <div className="text-[11px] tracking-[0.4em] text-zinc-300 mt-1">
              OPERATIONS · MAJESTY FOODS
            </div>
          </div>
        </div>
        <div>
          <div className="data-label text-zinc-400 mb-3">MajestyIO · Platform v1</div>
          <h2 className="font-display text-5xl font-black leading-none mb-6">
            Traceable.
            <br />
            Scannable.
            <br />
            <span style={{ color: "var(--mj-gold)" }}>Majestic.</span>
          </h2>
          <p className="text-zinc-300 max-w-md text-sm leading-relaxed">
            End-to-end pallet traceability — from cook line to the loading dock
            — with Code128 barcodes, 4×6 thermal labels, and shipment logs for
            every customer.
          </p>
        </div>
        <div className="text-[11px] tracking-[0.3em] text-zinc-400 uppercase">
          © {new Date().getFullYear()} MajestyIO · Majesty Foods
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-8 bg-[var(--mj-bg)]">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md"
          data-testid="login-form"
        >
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div
              className="w-10 h-10 flex items-center justify-center"
              style={{ background: "var(--mj-red)" }}
            >
              <Crown size={20} color="#E5B13A" strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-bold">Majesty<span style={{ color: "var(--mj-gold)" }}>IO</span></span>
          </div>
          <div className="data-label mb-3">Internal Access</div>
          <h1 className="font-display text-4xl md:text-5xl font-black mb-2">
            Sign in.
          </h1>
          <p className="text-sm text-[var(--mj-ink-muted)] mb-10">
            Authorized Majesty Foods personnel only.
          </p>

          <div className="space-y-5">
            <div>
              <label className="data-label block mb-2" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@majestyfoods.com"
                className="mj-input"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="data-label block mb-2" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mj-input"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="pill pill-expired w-full justify-center"
                style={{ padding: "10px" }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mj-btn mj-btn-primary w-full"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </div>

          <div className="mt-8 text-xs text-[var(--mj-ink-muted)] leading-relaxed">
            Need access? Contact your operations admin to create your account.
          </div>
        </form>
      </div>
    </div>
  );
}
